import { tap } from 'lodash/fp';
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useModalProviderContext } from '../ModalProvider';
import { installationSummaryModal } from '../ModalProvider/modalNames';
import CloseIcon from '../../themes/svg/closeIcon';
import cartAsMandiJob from '../../services/cartAsMandiJob';

import {
  ResponsiveInstallationSummaryModal,
  FlexModal,
  FlexJobOptions,
  FlexAddToCartButton,
  FlexDesignSummary,
  ModalTitle,
  FlexAddToCartButtonContainer,
  CloseModalButton,
  FlexProjectDetails,
} from './styles';
import useInstallationSummaryState from './useInstallationSummaryState';

const InstallationSummaryModal = ({
  designId,
  selectedProducts,
  selectedLeafItems,
  updateInstallationPricing,
  selectedProductOnly,
  setLoading,
  textOptInNumber,
  globalError,
  haulAwayCount,
  removalCount,
}) => {
  const { isOpen, setIsOpen } = useModalProviderContext(installationSummaryModal);
  const allProductsCount = selectedLeafItems.length;

  const [options, setOption, setOptions] = useInstallationSummaryState(
    isOpen,
    designId,
    selectedLeafItems,
    updateInstallationPricing,
    {
      windowHaul: !!haulAwayCount,
      windowRemoval: !!removalCount,
      windowHaulCount: haulAwayCount || allProductsCount,
      windowRemoveCount: removalCount || allProductsCount,
      textOptIn: true,
      textOptInNumber,
      specialInstructions: '',
    },
    ['windowHaul', 'windowRemoval', 'windowHaulCount', 'windowRemoveCount'],
  );

  useEffect(() => {
    setOptions(prev => ({
      ...prev,
      windowHaul: !!haulAwayCount,
      windowRemoval: !!removalCount,
      windowHaulCount: haulAwayCount || allProductsCount,
      windowRemoveCount: removalCount || allProductsCount,
    }));
  }, [haulAwayCount, removalCount]);

  const addToCart = useCallback(() => {
    setLoading(true);

    cartAsMandiJob({
      designId,
      selectedProducts,
      leafItems: selectedLeafItems,
      options: {
        createAndSaveJob: options,
      },
      productOnlyItems: selectedProductOnly,
    })
      .then(tap(() => setLoading(false)))
      .catch(globalError);
  }, [designId, selectedProducts, options, setLoading, globalError, selectedLeafItems]);

  const isDisabled = ({
    windowHaul, windowRemoval, windowHaulCount, windowRemoveCount,
  }) => {
    if (windowHaul === true && parseInt(windowHaulCount) === 0) {
      return true;
    }
    if (windowRemoval === true && parseInt(windowRemoveCount) === 0) {
      return true;
    }
    return false;
  };

  return (
    <ResponsiveInstallationSummaryModal
      id="summaryModal"
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      style={{
        overlay: {
          zIndex: 100,
          backgroundColor: 'rgba(0, 0, 0, .8)',
        },
      }}
    >
      <ModalTitle>
        Installation Summary
        <CloseModalButton
          id="closeModalButton"
          title="close"
          onClick={() => setIsOpen(false)}
          tabIndex="-1"
          role="button"
        >
          <CloseIcon />
        </CloseModalButton>
      </ModalTitle>
      <FlexModal id="flexModal">
        <FlexProjectDetails>
          <FlexJobOptions
            id="jobOptionsPanel"
            {...options}
            onOptionChange={setOption}
            totalWindowCount={allProductsCount}
          />
          <FlexAddToCartButtonContainer>
            <FlexAddToCartButton id="addToCartButton" onClick={addToCart} disabled={isDisabled(options)} />
          </FlexAddToCartButtonContainer>
        </FlexProjectDetails>

        <FlexDesignSummary id="designSummaryPanel" designId={designId} />
      </FlexModal>
    </ResponsiveInstallationSummaryModal>
  );
};
InstallationSummaryModal.propTypes = {
  designId: PropTypes.string.isRequired,
  selectedProducts: PropTypes.arrayOf(PropTypes.shape).isRequired,
  selectedLeafItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateInstallationPricing: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  selectedProductOnly: PropTypes.arrayOf(PropTypes.shape).isRequired,
  globalError: PropTypes.func.isRequired,
  textOptInNumber: PropTypes.string.isRequired,
  haulAwayCount: PropTypes.number.isRequired,
  removalCount: PropTypes.number.isRequired,
};
export default InstallationSummaryModal;
